// Cloud Management (Juwan 2026-11-03) — two tabs: Azure Resource Map + Azure Cost Management.
// Only Juwan and Andrew: the API 404s everyone else (backend/src/cloudMgmt.js) and the nav item is gated on it.
// READ-ONLY. The numbers are the same reconciled snapshot the Monday Weekly Cost Audit PDF is built from
// (vm-audit/build.mjs → audit.json): every resource sits under exactly one server or group (shared items split),
// and the totals add up to the Azure bill to the cent.
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw, Server, HardDrive, Network, ShieldCheck, Database, Activity, Box, AlertTriangle, ExternalLink, X, Search, ChevronDown, ChevronRight, CheckCircle2, Cpu } from "lucide-react";
import API from "../api";
import { tokens } from "../theme";
import { useTheme } from "../context/ThemeContext";
import useIsMobile from "../hooks/useIsMobile";

const TENANT = "72f1a79a-d4c0-4c88-a0a3-c9b509fe2868";
const SUB_NAME = { "389c6c22-9d9d-4c4f-98fc-bd827d6fbd6d": "Summaware Subscription", "2b60d8ce-397b-4159-9142-f938071d7ab4": "Azure subscription 1", "22c314e4-3b07-4ad0-930e-4e69d44017bc": "MIDTC" };

// Same server colours as the Weekly Cost Audit PDF (validated categorical slots 1–5; dark = the dark-surface steps).
const OWNER_COLORS = {
  WEB:  { light: "#2a78d6", dark: "#3987e5" },
  LIC:  { light: "#eb6834", dark: "#d95926" },
  EDIT: { light: "#1baf7a", dark: "#199e70" },
  TICK: { light: "#eda100", dark: "#c98500" },
  FFTC: { light: "#e87ba4", dark: "#d55181" },
};
const NONE_COLOR = { light: "#6f6e69", dark: "#8a8980" };
const DAILY_COLORS = { usage: { light: "#6250d6", dark: "#9085e9" }, other: { light: "#008300", dark: "#008300" } };

const usd = (n) => (n < 0 ? "-$" : "$") + Math.abs(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd0 = (n) => (n < 0 ? "-$" : "$") + Math.round(Math.abs(Number(n) || 0)).toLocaleString("en-US");
const isWarn = (f) => /unused|not attached|leftover|deleted/i.test(f);
const dayLabel = (k) => new Date(Date.UTC(Math.floor(k / 10000), Math.floor(k / 100) % 100 - 1, k % 100)).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
const portalUrl = (id) => `https://portal.azure.com/#@${TENANT}/resource${id}`;
// Reservations never appear in Resource Graph, so `exists` is false for them — they are still live.
const present = (r) => r.exists || r.t === "microsoft.capacity/reservationorders";

const CATS = [
  { key: "compute",  label: "Compute",           icon: Cpu,         test: (t) => /^microsoft\.compute\/(virtualmachines|sshpublickeys)|^microsoft\.capacity\//.test(t) },
  { key: "storage",  label: "Disks & storage",   icon: HardDrive,   test: (t) => /^microsoft\.compute\/(disks|snapshots|restorepointcollections|galleries)|^microsoft\.storage\//.test(t) },
  { key: "network",  label: "Network",           icon: Network,     test: (t) => /^microsoft\.network\//.test(t) },
  { key: "backup",   label: "Backup & recovery", icon: ShieldCheck, test: (t) => /^microsoft\.(recoveryservices|datareplication)\//.test(t) },
  { key: "database", label: "Database",          icon: Database,    test: (t) => /^microsoft\.(dbforpostgresql|dbformysql|sql)\//.test(t) },
  { key: "monitor",  label: "Monitoring",        icon: Activity,    test: (t) => /^microsoft\.(insights|alertsmanagement|operationalinsights)\//.test(t) },
  { key: "other",    label: "Other",             icon: Box,         test: () => true },
];
const catOf = (t) => (t === "zero" ? CATS.find((c) => c.key === "other") : CATS.find((c) => c.test(t)));

const card = { background: tokens.surface.panel, border: `1px solid ${tokens.border.soft}`, borderRadius: tokens.radius.md, boxShadow: tokens.shadow.panel };
const btn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", fontSize: 12.5, fontWeight: 600, borderRadius: tokens.radius.sm, border: `1px solid ${tokens.border.mid}`, background: tokens.surface.input, color: tokens.text.body, cursor: "pointer", fontFamily: "inherit" };

function Chip({ text }) {
  const warn = isWarn(text);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10.5, lineHeight: "15px", padding: "0 6px", borderRadius: 8, marginRight: 4, marginTop: 3,
      background: warn ? "color-mix(in srgb, #f59e0b 14%, transparent)" : tokens.surface.mid,
      color: warn ? "var(--t-text-primary)" : tokens.text.muted, border: `1px solid ${warn ? "color-mix(in srgb, #f59e0b 45%, transparent)" : tokens.border.soft}` }}>
      {warn && <AlertTriangle size={10} color="#f59e0b" aria-label="needs a look" />}{text}
    </span>
  );
}

// ───────────────────────────── page ─────────────────────────────
export default function CloudManagement() {
  const isMobile = useIsMobile();
  const { mode } = useTheme();
  const dark = mode === "dark";
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "cost" ? "cost" : "map";
  const setTab = (t) => setParams(t === "map" ? {} : { tab: t }, { replace: true });

  const [snap, setSnap] = useState(null);
  const [st, setSt] = useState({ refreshing: false, lastError: null });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => API.get("/cloud/snapshot").then(({ data }) => {
    setSnap(data.snapshot); setSt(data); setDenied(false);
  }).catch((e) => { if (e?.response?.status === 404) setDenied(true); else setMsg("Couldn't load the cloud data. " + (e?.message || "")); })
    .finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  // While a refresh runs, check every 10 s and reload the snapshot when it finishes.
  useEffect(() => {
    if (!st.refreshing) return undefined;
    const t = setInterval(() => {
      API.get("/cloud/status").then(({ data }) => { if (!data.refreshing) load(); else setSt((s) => ({ ...s, ...data })); }).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [st.refreshing, load]);

  const refresh = () => {
    setMsg("");
    API.post("/cloud/refresh").then(({ data }) => setSt((s) => ({ ...s, ...data })))
      .catch((e) => setMsg(e?.response?.data?.error || "Couldn't start the refresh."));
  };

  if (denied) return <div style={{ padding: 32, color: tokens.text.muted }}>This page isn't available.</div>;

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth: 1400, width: "100%", minWidth: 0, boxSizing: "border-box", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: tokens.text.primary }}>Cloud Management</h1>
          <div style={{ fontSize: 13, color: tokens.text.faint, marginTop: 3 }}>
            Azure resources and what they cost. View only. Nothing here changes Azure.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {snap && <span style={{ fontSize: 12, color: tokens.text.faint }}>Data as of {snap.pulledEastern} ET</span>}
          <button type="button" onClick={refresh} disabled={st.refreshing} style={{ ...btn, opacity: st.refreshing ? 0.7 : 1, cursor: st.refreshing ? "default" : "pointer" }}>
            <RefreshCw size={14} style={st.refreshing ? { animation: "spin 1s linear infinite" } : undefined} />
            {st.refreshing ? "Checking Azure…" : "Refresh from Azure"}
          </button>
        </div>
      </div>
      {st.refreshing && <Note>Getting fresh numbers from Azure. This takes 2 to 4 minutes. You can keep using the page.</Note>}
      {st.lastError && !st.refreshing && <Note warn>{st.lastError}</Note>}
      {msg && <Note warn>{msg}</Note>}

      <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${tokens.border.soft}`, margin: "10px 0 18px" }} role="tablist">
        {[{ k: "map", l: "Azure Resource Map" }, { k: "cost", l: "Azure Cost Management" }].map((t) => {
          const active = tab === t.k;
          return (
            <button key={t.k} type="button" role="tab" aria-selected={active} onClick={() => setTab(t.k)} style={{
              padding: "9px 16px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", background: "none", border: "none", fontFamily: "inherit",
              borderBottom: `2px solid ${active ? tokens.accent.indigo2 : "transparent"}`, marginBottom: -1,
              color: active ? tokens.accent.indigo2 : tokens.text.faint,
            }}>{t.l}</button>
          );
        })}
      </div>

      {loading && <div style={{ color: tokens.text.faint, fontSize: 13, padding: 24 }}>Loading…</div>}
      {!loading && !snap && (
        <div style={{ ...card, padding: 24, color: tokens.text.muted, fontSize: 13.5 }}>
          {st.refreshing ? "Getting the first snapshot from Azure. This takes 2 to 4 minutes." : "No data yet. Click Refresh from Azure."}
        </div>
      )}
      {snap && tab === "map" && <ResourceMap snap={snap} dark={dark} isMobile={isMobile} />}
      {snap && tab === "cost" && <CostView snap={snap} dark={dark} isMobile={isMobile} />}
    </div>
  );
}

function Note({ children, warn }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.5, padding: "8px 12px", borderRadius: tokens.radius.sm, margin: "8px 0",
      background: warn ? "color-mix(in srgb, #f59e0b 12%, transparent)" : tokens.surface.mid, color: tokens.text.body,
      border: `1px solid ${warn ? "color-mix(in srgb, #f59e0b 40%, transparent)" : tokens.border.soft}` }}>
      {warn ? <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} /> : <RefreshCw size={14} style={{ flexShrink: 0, marginTop: 2, animation: "spin 1s linear infinite" }} />}
      <span>{children}</span>
    </div>
  );
}

const ownerColor = (key, dark) => (OWNER_COLORS[key] ? OWNER_COLORS[key][dark ? "dark" : "light"] : NONE_COLOR[dark ? "dark" : "light"]);

// ───────────────────────────── Resource map ─────────────────────────────
function ResourceMap({ snap, dark, isMobile }) {
  const [view, setView] = useState("servers");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const resById = useMemo(() => Object.fromEntries((snap.resources || []).map((r) => [r.id, r])), [snap]);
  const vms = snap.owners.filter((o) => o.isVm);
  const groups = snap.owners.filter((o) => !o.isVm);
  const flagged = snap.owners.flatMap((o) => o.rows.filter((r) => r.flags.some(isWarn)));
  const P = snap.period;
  const needle = q.trim().toLowerCase();
  const match = (r) => !needle || `${r.name} ${r.type} ${r.detail} ${r.flags.join(" ")}`.toLowerCase().includes(needle);

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 2, background: tokens.surface.mid, padding: 3, borderRadius: tokens.radius.sm, border: `1px solid ${tokens.border.soft}` }}>
          {[{ k: "servers", l: "By server" }, { k: "list", l: "All resources" }].map((v) => (
            <button key={v.k} type="button" onClick={() => setView(v.k)} style={{ padding: "5px 12px", fontSize: 12.5, fontWeight: 600, border: "none", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
              background: view === v.k ? tokens.surface.panel : "transparent", color: view === v.k ? tokens.text.primary : tokens.text.faint, boxShadow: view === v.k ? tokens.shadow.panel : "none" }}>{v.l}</button>
          ))}
        </div>
        <label style={{ position: "relative", flex: isMobile ? "1 1 100%" : "0 1 320px" }}>
          <Search size={14} style={{ position: "absolute", left: 10, top: 9, color: "var(--t-text-faint)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a resource, type or IP…" aria-label="Find a resource"
            style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px 7px 30px", fontSize: 13, borderRadius: tokens.radius.sm, border: `1px solid ${tokens.border.mid}`, background: tokens.surface.input, color: tokens.text.primary, fontFamily: "inherit" }} />
        </label>
        <div style={{ fontSize: 12.5, color: tokens.text.faint, display: "flex", gap: 14, flexWrap: "wrap" }}>
          <span><b style={{ color: tokens.text.primary }}>{snap.counts.resources}</b> resources</span>
          <span><b style={{ color: tokens.text.primary }}>{snap.counts.billed}</b> billed</span>
          <span><b style={{ color: tokens.text.primary }}>{vms.length}</b> servers</span>
          {flagged.length > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><AlertTriangle size={12} color="#f59e0b" /><b style={{ color: tokens.text.primary }}>{flagged.length}</b> to look at</span>}
        </div>
      </div>

      {view === "servers" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(420px, 1fr))", gap: 14 }}>
            {vms.map((o) => <ServerMap key={o.key} o={o} P={P} dark={dark} resById={resById} match={match} filtering={!!needle} onPick={setSel} />)}
          </div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: tokens.text.primary, margin: "24px 0 4px" }}>Not tied to a server</h3>
          <div style={{ fontSize: 12.5, color: tokens.text.faint, marginBottom: 10 }}>
            Shared services and what's left of retired servers. {usd(snap.none.prev)} in {P.prevShort} · {usd(snap.none.cur)} so far in {P.curShort}.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(420px, 1fr))", gap: 14 }}>
            {groups.map((o) => <ServerMap key={o.key} o={o} P={P} dark={dark} resById={resById} match={match} filtering={!!needle} onPick={setSel} group />)}
          </div>
        </>
      ) : (
        <ResourceList snap={snap} needle={needle} dark={dark} isMobile={isMobile} onPick={setSel} />
      )}
      {sel && <Drawer sel={sel} P={P} resById={resById} onClose={() => setSel(null)} isMobile={isMobile} snap={snap} />}
    </div>
  );
}

function ServerMap({ o, P, dark, resById, match, filtering, onPick, group }) {
  const [open, setOpen] = useState(!group);
  const color = ownerColor(o.key, dark);
  const rows = o.rows.filter(match);
  const vm = !group && Object.values(resById).find((r) => r.t === "microsoft.compute/virtualmachines" && r.owners.includes(o.key) && r.exists);
  if (filtering && !rows.length) return null;
  const show = open || filtering;
  const byCat = CATS.map((c) => ({ ...c, rows: rows.filter((r) => catOf(r.t).key === c.key) })).filter((c) => c.rows.length);
  const warnN = o.rows.filter((r) => r.flags.some(isWarn)).length;
  const power = vm?.power ? vm.power.replace("PowerState/", "") : "";

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", minWidth: 0 }}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={show} style={{ display: "flex", width: "100%", alignItems: "center", gap: 10, padding: "12px 14px", background: "none", border: "none", borderBottom: show ? `1px solid ${tokens.border.soft}` : "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
        <span style={{ width: 34, height: 34, borderRadius: 8, background: `color-mix(in srgb, ${color} 18%, transparent)`, border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {group ? <Box size={16} color={color} /> : <Server size={16} color={color} />}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: tokens.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: group ? "normal" : "nowrap" }}>{o.name}</span>
          <span style={{ display: "block", fontSize: 11.5, color: tokens.text.faint, marginTop: 1 }}>
            {vm ? [vm.vmSize?.replace("Standard_", ""), vm.osType, SUB_NAME[vm.sub]].filter(Boolean).join(" · ") : `${o.resources} resource${o.resources === 1 ? "" : "s"}`}
            {power && <span style={{ marginLeft: 6, color: power === "running" ? tokens.text.body : "#f59e0b" }}>● {power}</span>}
            {warnN > 0 && <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 3 }}><AlertTriangle size={11} color="#f59e0b" />{warnN} to look at</span>}
          </span>
        </span>
        <span style={{ textAlign: "right", flexShrink: 0 }}>
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: tokens.text.primary, fontVariantNumeric: "tabular-nums" }}>{usd(o.prev)}</span>
          <span style={{ display: "block", fontSize: 11, color: tokens.text.faint }}>{P.prevShort} · {usd(o.cur)} {P.curShort} so far</span>
        </span>
        {show ? <ChevronDown size={15} color="var(--t-text-faint)" /> : <ChevronRight size={15} color="var(--t-text-faint)" />}
      </button>
      {show && (
        <div style={{ padding: "10px 14px 14px" }}>
          {byCat.map((c, ci) => (
            <div key={c.key} style={{ position: "relative", paddingLeft: 18, marginTop: ci ? 10 : 2 }}>
              {/* trunk + branch connectors */}
              <span aria-hidden="true" style={{ position: "absolute", left: 5, top: ci ? -10 : 0, bottom: ci === byCat.length - 1 ? "calc(100% - 11px)" : 0, borderLeft: `2px solid color-mix(in srgb, ${color} 45%, transparent)` }} />
              <span aria-hidden="true" style={{ position: "absolute", left: 5, top: 10, width: 10, borderTop: `2px solid color-mix(in srgb, ${color} 45%, transparent)` }} />
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: tokens.text.label, marginBottom: 5 }}>
                <c.icon size={13} /> {c.label}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 5 }}>
                {c.rows.map((r, i) => <Node key={i} r={r} P={P} color={color} onClick={() => onPick({ row: r, owner: o })} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Node({ r, P, color, onClick }) {
  const billed = Math.abs(r.prev) + Math.abs(r.cur) >= 0.005;
  return (
    <button type="button" onClick={onClick} style={{ display: "flex", gap: 10, alignItems: "flex-start", width: "100%", minWidth: 0, boxSizing: "border-box", textAlign: "left", padding: "7px 10px", borderRadius: tokens.radius.sm, cursor: "pointer", fontFamily: "inherit",
      background: tokens.surface.mid, border: `1px solid ${tokens.border.soft}`, borderLeft: `3px solid ${billed ? color : tokens.border.mid}` }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: tokens.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
        <span style={{ display: "block", fontSize: 11, color: tokens.text.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.detail}</span>
        {r.flags.length > 0 && <span style={{ display: "block" }}>{r.flags.map((f, i) => <Chip key={i} text={f} />)}</span>}
      </span>
      <span style={{ textAlign: "right", flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
        <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: billed ? tokens.text.body : tokens.text.faint }}>{billed ? usd(r.prev) : "no charge"}</span>
        {billed && <span style={{ display: "block", fontSize: 10.5, color: tokens.text.faint }}>{usd(r.cur)} {P.curShort}</span>}
      </span>
    </button>
  );
}

function ResourceList({ snap, needle, dark, isMobile, onPick }) {
  const [owner, setOwner] = useState("");
  const [sub, setSub] = useState("");
  const [billedOnly, setBilledOnly] = useState(false);
  const [sort, setSort] = useState({ k: "prev", dir: -1 });
  const ownerName = useMemo(() => Object.fromEntries(snap.owners.map((o) => [o.key, o.name])), [snap]);
  const P = snap.period;
  const list = useMemo(() => (snap.resources || []).filter((r) => {
    if (owner && !r.owners.includes(owner)) return false;
    if (sub && r.sub !== sub) return false;
    if (billedOnly && Math.abs(r.prev) + Math.abs(r.cur) < 0.005) return false;
    if (needle && !`${r.name} ${r.type} ${r.rg} ${r.detail} ${r.location}`.toLowerCase().includes(needle)) return false;
    return true;
  }).sort((a, b) => {
    const k = sort.k;
    const av = k === "owner" ? (ownerName[a.owners[0]] || "") : a[k], bv = k === "owner" ? (ownerName[b.owners[0]] || "") : b[k];
    if (typeof av === "number") return (av - bv) * sort.dir;
    return String(av || "").localeCompare(String(bv || "")) * sort.dir;
  }), [snap, owner, sub, billedOnly, needle, sort, ownerName]);
  const sum = (p) => list.reduce((a, r) => a + r[p], 0);
  const sel = { padding: "6px 8px", fontSize: 12.5, borderRadius: tokens.radius.sm, border: `1px solid ${tokens.border.mid}`, background: tokens.surface.input, color: tokens.text.primary, fontFamily: "inherit" };
  const H = ({ k, l, right }) => (
    <th onClick={() => setSort((s) => ({ k, dir: s.k === k ? -s.dir : (k === "prev" || k === "cur" ? -1 : 1) }))} style={{ textAlign: right ? "right" : "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: tokens.text.label, cursor: "pointer", whiteSpace: "nowrap", borderBottom: `1px solid ${tokens.border.soft}` }}>
      {l}{sort.k === k ? (sort.dir < 0 ? " ↓" : " ↑") : ""}
    </th>
  );
  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <select value={owner} onChange={(e) => setOwner(e.target.value)} style={sel} aria-label="Belongs to">
          <option value="">Every server and group</option>
          {snap.owners.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
        </select>
        <select value={sub} onChange={(e) => setSub(e.target.value)} style={sel} aria-label="Subscription">
          <option value="">All subscriptions</option>
          {Object.entries(SUB_NAME).map(([id, n]) => <option key={id} value={id}>{n}</option>)}
        </select>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, color: tokens.text.body, cursor: "pointer" }}>
          <input type="checkbox" checked={billedOnly} onChange={(e) => setBilledOnly(e.target.checked)} /> Only resources with a charge
        </label>
        <span style={{ fontSize: 12, color: tokens.text.faint, marginLeft: "auto" }}>{list.length} shown · {usd(sum("prev"))} {P.prevShort} · {usd(sum("cur"))} {P.curShort} so far</span>
      </div>
      <div style={{ ...card, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: isMobile ? 760 : 0 }}>
          <thead><tr>
            <H k="name" l="Resource" /><H k="type" l="Type" /><H k="owner" l="Belongs to" />
            <H k="prev" l={`${P.prevShort} ${P.prevYear}`} right /><H k="cur" l={`${P.curShort} so far`} right />
            <H k="rg" l="Resource group" /><H k="location" l="Region" />
          </tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} onClick={() => onPick({ resource: r })} style={{ cursor: "pointer", borderBottom: `1px solid ${tokens.border.softer}` }}>
                <td style={{ padding: "7px 10px", color: tokens.text.primary, fontWeight: 600, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.name}{!present(r) && <span style={{ marginLeft: 6, fontSize: 10.5, color: tokens.text.faint, fontWeight: 400 }}>deleted</span>}
                </td>
                <td style={{ padding: "7px 10px", color: tokens.text.muted, maxWidth: 190 }}>{r.type}</td>
                <td style={{ padding: "7px 10px", color: tokens.text.body, minWidth: 210, maxWidth: 340 }}>
                  {r.owners.map((k) => (
                    <span key={k} style={{ display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.45 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: ownerColor(k, dark), flexShrink: 0, marginTop: 5 }} />{ownerName[k] || k}
                    </span>
                  ))}
                </td>
                <td style={{ padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: tokens.text.body, whiteSpace: "nowrap" }}>{usd(r.prev)}</td>
                <td style={{ padding: "7px 10px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: tokens.text.body, whiteSpace: "nowrap" }}>{usd(r.cur)}</td>
                <td style={{ padding: "7px 10px", color: tokens.text.muted, whiteSpace: "nowrap" }}>{r.rg || "—"}</td>
                <td style={{ padding: "7px 10px", color: tokens.text.muted, whiteSpace: "nowrap" }}>{r.location || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11.5, color: tokens.text.faint, marginTop: 8 }}>Each resource's full charge. A resource shared by several servers (a backup vault, a network) shows its whole charge here; the server view splits it.</div>
    </div>
  );
}

function Drawer({ sel, P, resById, onClose, isMobile, snap }) {
  const items = sel.resource ? [sel.resource] : sel.row.ids.map((id) => resById[id]).filter(Boolean);
  const head = sel.resource || sel.row;
  const ownerName = Object.fromEntries(snap.owners.map((o) => [o.key, o.name]));
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div role="dialog" aria-label={head.name} style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end", alignItems: isMobile ? "flex-end" : "stretch", background: "rgba(0,0,0,0.35)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, borderRadius: isMobile ? "14px 14px 0 0" : 0, width: isMobile ? "100%" : 440, maxHeight: isMobile ? "80vh" : "100vh", overflowY: "auto", padding: 18, boxSizing: "border-box" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: tokens.text.primary, wordBreak: "break-word" }}>{head.name}</div>
            <div style={{ fontSize: 12, color: tokens.text.faint, marginTop: 2 }}>{head.detail}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ ...btn, padding: 6 }}><X size={15} /></button>
        </div>
        <div style={{ display: "flex", gap: 10, margin: "14px 0" }}>
          {[{ l: `${P.prevLong} ${P.prevYear}`, v: head.prev }, { l: `${P.curLong} so far`, v: head.cur }].map((x) => (
            <div key={x.l} style={{ flex: 1, padding: "10px 12px", borderRadius: tokens.radius.sm, background: tokens.surface.mid, border: `1px solid ${tokens.border.soft}` }}>
              <div style={{ fontSize: 11, color: tokens.text.faint }}>{x.l}</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: tokens.text.primary, fontVariantNumeric: "tabular-nums" }}>{usd(x.v)}</div>
            </div>
          ))}
        </div>
        {sel.row && sel.owner && <div style={{ fontSize: 12.5, color: tokens.text.body, marginBottom: 6 }}>Counted under <b>{sel.owner.name}</b>.</div>}
        {head.flags?.length > 0 && <div style={{ marginBottom: 8 }}>{head.flags.map((f, i) => <Chip key={i} text={f} />)}</div>}
        {head.note && <div style={{ fontSize: 12.5, color: tokens.text.body, lineHeight: 1.5, marginBottom: 10 }}>{head.note}</div>}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: tokens.text.label, margin: "14px 0 6px" }}>
          {items.length === 1 ? "Resource" : `${items.length} resources`}
        </div>
        <div style={{ display: "grid", gap: 6 }}>
          {items.map((r) => (
            <div key={r.id} style={{ padding: "8px 10px", borderRadius: tokens.radius.sm, border: `1px solid ${tokens.border.soft}`, fontSize: 12 }}>
              <div style={{ fontWeight: 600, color: tokens.text.primary, wordBreak: "break-word" }}>{r.name}</div>
              <div style={{ color: tokens.text.faint, marginTop: 2, lineHeight: 1.5 }}>
                {r.type}{r.rg ? ` · ${r.rg} group` : ""}{r.location ? ` · ${r.location}` : ""}{SUB_NAME[r.sub] ? ` · ${SUB_NAME[r.sub]}` : ""}
                {sel.resource && r.owners.length > 0 && <><br />Belongs to {r.owners.map((k) => ownerName[k]).join(", ")}</>}
                {sel.row && items.length > 1 && <><br />{usd(r.prev)} {P.prevShort} · {usd(r.cur)} {P.curShort} so far (full charge)</>}
              </div>
              {present(r) ? (
                <a href={portalUrl(r.id)} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 5, fontSize: 12, color: tokens.accent.indigo2, textDecoration: "none" }}>
                  Open in Azure portal <ExternalLink size={12} />
                </a>
              ) : <div style={{ marginTop: 4, color: tokens.text.faint }}>No longer in Azure (billed before it was deleted).</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────── Cost management ─────────────────────────────
function CostView({ snap, dark, isMobile }) {
  const P = snap.period;
  const projUp = snap.projected > snap.total.prev;
  const tiles = [
    { l: `${P.prevLong} ${P.prevYear}`, v: usd(snap.total.prev), s: "Last full month, the whole bill" },
    { l: `${P.curLong} so far`, v: usd(snap.total.cur), s: `Day ${snap.daysElapsed} of ${snap.daysInMonth}. Azure posts costs a day or two late.` },
    { l: `Projected ${P.curLong}`, v: usd0(snap.projected), s: `${projUp ? "Up" : "Down"} ${Math.abs(snap.trendPct).toFixed(0)}% vs ${P.prevShort}`, title: `Projection = ${snap.projBasis}, plus the monthly reservation charges (${usd(snap.resMonthly)}).` },
    { l: "Saved per month", v: usd0(snap.saved), s: `vs ${snap.baseline.label} (${usd0(snap.baseline.total)}), before the cleanup · ~${usd0(snap.saved * 12)}/yr`, good: snap.saved > 0 },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))", gap: 12 }}>
        {tiles.map((t) => (
          <div key={t.l} title={t.title} style={{ ...card, padding: "14px 16px", minWidth: 0 }}>
            <div style={{ fontSize: 12, color: tokens.text.faint, fontWeight: 600 }}>{t.l}</div>
            <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: tokens.text.primary, fontVariantNumeric: "tabular-nums", margin: "4px 0 2px", display: "flex", alignItems: "center", gap: 6 }}>
              {t.v}{t.good && <CheckCircle2 size={16} color={tokens.accent.green} aria-label="saving" />}
            </div>
            <div style={{ fontSize: 11.5, color: tokens.text.faint, lineHeight: 1.4 }}>{t.s}</div>
          </div>
        ))}
      </div>
      <DailyChart snap={snap} dark={dark} isMobile={isMobile} />
      <ByServer snap={snap} dark={dark} isMobile={isMobile} />
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 3fr) minmax(0, 2fr)", gap: 16 }}>
        <ByService snap={snap} />
        <BySub snap={snap} />
      </div>
      <div style={{ fontSize: 11.5, color: tokens.text.faint, lineHeight: 1.6 }}>
        Source: Azure Cost Management (actual cost, by resource) and Azure Resource Graph, read by the ticketing server's own read-only Azure identity.
        The same numbers as the Monday Weekly Cost Audit. Every charge is counted under one server or group, and the totals match the Azure bill.
        Projection = {snap.projBasis}, plus the monthly reservation charges ({usd(snap.resMonthly)}).
      </div>
    </div>
  );
}

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(300);   // start small: the container sets the width, never the chart
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.floor(e.contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function DailyChart({ snap, dark, isMobile }) {
  const [ref, W] = useWidth();
  const [hover, setHover] = useState(null);
  const days = snap.daily || [];
  const pulled = useMemo(() => {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", year: "numeric", month: "numeric", day: "numeric" }).formatToParts(new Date(snap.pulledAt)).map((x) => [x.type, x.value]));
    return Date.UTC(+p.year, +p.month - 1, +p.day);
  }, [snap.pulledAt]);
  const keyMs = (k) => Date.UTC(Math.floor(k / 10000), Math.floor(k / 100) % 100 - 1, k % 100);
  const settledKey = (k) => keyMs(k) <= pulled - 2 * 86400000;
  const settled = days.filter((d) => settledKey(d.date)).slice(-7);
  const avg = settled.length ? settled.reduce((a, d) => a + d.usage, 0) / settled.length : null;
  if (!days.length) return null;
  const cU = DAILY_COLORS.usage[dark ? "dark" : "light"], cO = DAILY_COLORS.other[dark ? "dark" : "light"];
  const H = isMobile ? 200 : 240, padL = 44, padR = 8, padT = 10, padB = 26;
  const max = Math.max(...days.map((d) => d.usage + Math.max(0, d.other)), 1);
  const step = max > 200 ? 50 : max > 80 ? 25 : 10;
  const top = Math.ceil(max / step) * step;
  const y = (v) => padT + (H - padT - padB) * (1 - v / top);
  const bw = (W - padL - padR) / days.length;
  const barW = Math.max(3, bw - 2);
  const ticks = []; for (let v = 0; v <= top; v += step) ticks.push(v);
  const labelEvery = Math.ceil(days.length / (W < 520 ? 6 : 12));
  return (
    <div style={{ ...card, padding: "14px 16px", minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "baseline" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: tokens.text.primary }}>Cost per day</div>
          <div style={{ fontSize: 12, color: tokens.text.faint }}>
            Last {days.length} days, all subscriptions.{avg != null && <> Recent settled days average <b style={{ color: tokens.text.body }}>{usd(avg)}/day</b> in usage.</>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, fontSize: 12, color: tokens.text.body }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: cU }} />Usage</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: cO }} />Reservation charges</span>
        </div>
      </div>
      <div ref={ref} style={{ position: "relative", marginTop: 8, width: "100%", minWidth: 0, overflow: "hidden" }} onMouseLeave={() => setHover(null)}>
        <svg width={W} height={H} role="img" aria-label="Cost per day bar chart" style={{ display: "block" }}>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="var(--t-border-soft)" strokeWidth="1" />
              <text x={padL - 6} y={y(v) + 4} textAnchor="end" fontSize="10.5" fill="var(--t-text-faint)">${v}</text>
            </g>
          ))}
          {days.map((d, i) => {
            const x = padL + i * bw + (bw - barW) / 2;
            const unsettled = !settledKey(d.date);
            const uTop = y(Math.max(0, d.usage));
            const oH = Math.max(0, d.other) > 0 ? y(0) - y(d.other) : 0;
            const hoverOn = hover === i;
            return (
              <g key={d.date} opacity={unsettled ? 0.45 : 1}>
                <rect x={padL + i * bw} y={padT} width={bw} height={H - padT - padB} fill={hoverOn ? "var(--t-surface-mid)" : "transparent"} onMouseEnter={() => setHover(i)} onClick={() => setHover(i)} />
                <rect x={x} y={uTop} width={barW} height={Math.max(0, y(0) - uTop)} rx={Math.min(3, barW / 2)} fill={cU} pointerEvents="none" />
                {oH > 0 && <rect x={x} y={uTop - oH - 2} width={barW} height={Math.max(1, oH)} rx={Math.min(3, barW / 2)} fill={cO} pointerEvents="none" />}
                {i % labelEvery === 0 && <text x={x + barW / 2} y={H - 8} textAnchor="middle" fontSize="10.5" fill="var(--t-text-faint)">{dayLabel(d.date)}</text>}
              </g>
            );
          })}
          <line x1={padL} x2={W - padR} y1={y(0)} y2={y(0)} stroke="var(--t-border-strong)" strokeWidth="1" />
        </svg>
        {hover != null && days[hover] && (() => {
          const d = days[hover];
          const left = Math.min(Math.max(padL + hover * bw + bw / 2 - 90, 0), W - 180);
          return (
            <div style={{ position: "absolute", top: 0, left, width: 180, pointerEvents: "none", padding: "8px 10px", fontSize: 12, borderRadius: tokens.radius.sm, background: tokens.surface.panelHi, border: `1px solid ${tokens.border.mid}`, boxShadow: tokens.shadow.panel, color: tokens.text.body }}>
              <div style={{ fontWeight: 700, color: tokens.text.primary, marginBottom: 3 }}>{dayLabel(d.date)}{!settledKey(d.date) && <span style={{ fontWeight: 400, color: tokens.text.faint }}> · still filling in</span>}</div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span>Usage</span><b style={{ fontVariantNumeric: "tabular-nums" }}>{usd(d.usage)}</b></div>
              {Math.abs(d.other) >= 0.005 && <div style={{ display: "flex", justifyContent: "space-between" }}><span>Reservation charges</span><b style={{ fontVariantNumeric: "tabular-nums" }}>{usd(d.other)}</b></div>}
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${tokens.border.soft}`, marginTop: 3, paddingTop: 3 }}><span>Total</span><b style={{ fontVariantNumeric: "tabular-nums" }}>{usd(d.usage + d.other)}</b></div>
            </div>
          );
        })()}
      </div>
      <div style={{ fontSize: 11.5, color: tokens.text.faint, marginTop: 4 }}>Faded bars are the last two days. Azure is still adding their costs.</div>
    </div>
  );
}

function ByServer({ snap, dark, isMobile }) {
  const [open, setOpen] = useState({});
  const P = snap.period;
  const vms = snap.owners.filter((o) => o.isVm);
  const groups = snap.owners.filter((o) => !o.isVm);
  const lines = [...vms.map((o) => ({ key: o.key, name: o.name, prev: o.prev, cur: o.cur, n: o.resources, rows: o.rows })),
    { key: "NONE", name: "Not tied to a server", prev: snap.none.prev, cur: snap.none.cur, n: groups.reduce((a, g) => a + g.resources, 0), groups }];
  const max = Math.max(...lines.map((l) => l.prev), 1);
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));
  const numCol = { width: isMobile ? 78 : 100, textAlign: "right", fontVariantNumeric: "tabular-nums", flexShrink: 0 };
  return (
    <div style={{ ...card, padding: "14px 16px" }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: tokens.text.primary }}>Cost by server</div>
      <div style={{ fontSize: 12, color: tokens.text.faint, marginBottom: 10 }}>Everything each server uses: disks, network, backups, and the reservation that pays for its compute. Click a row to see the items.</div>
      <div style={{ display: "flex", gap: 10, padding: "0 0 6px", fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: tokens.text.label, borderBottom: `1px solid ${tokens.border.soft}` }}>
        <span style={{ flex: 1 }}>Server</span><span style={numCol}>{P.prevShort} {P.prevYear}</span><span style={numCol}>{P.curShort} so far</span>
      </div>
      {lines.map((l) => {
        const c = ownerColor(l.key, dark);
        return (
          <div key={l.key} style={{ borderBottom: `1px solid ${tokens.border.softer}` }}>
            <button type="button" onClick={() => toggle(l.key)} aria-expanded={!!open[l.key]} style={{ display: "flex", gap: 10, alignItems: "center", width: "100%", padding: "9px 0", background: "none", border: "none", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
              {open[l.key] ? <ChevronDown size={14} color="var(--t-text-faint)" /> : <ChevronRight size={14} color="var(--t-text-faint)" />}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: tokens.text.primary }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: c, flexShrink: 0 }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                  {!isMobile && <span style={{ fontSize: 11, color: tokens.text.faint, fontWeight: 400, flexShrink: 0 }}>{l.n} resources</span>}
                </span>
                <span style={{ display: "block", height: 6, marginTop: 5, borderRadius: 3, background: tokens.surface.mid, overflow: "hidden" }}>
                  <span style={{ display: "block", height: "100%", width: `${Math.max(0.5, (l.prev / max) * 100)}%`, background: c, borderRadius: 3 }} />
                </span>
              </span>
              <span style={{ ...numCol, fontSize: 13, fontWeight: 700, color: tokens.text.primary }}>{usd(l.prev)}</span>
              <span style={{ ...numCol, fontSize: 13, color: tokens.text.body }}>{usd(l.cur)}</span>
            </button>
            {open[l.key] && (
              <div style={{ padding: "0 0 10px 24px" }}>
                {(l.groups ? l.groups.map((g) => ({ name: g.name, detail: `${g.resources} resources`, prev: g.prev, cur: g.cur, flags: [] })) : l.rows).map((r, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "5px 0", borderTop: i ? `1px dashed ${tokens.border.softer}` : "none" }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, color: tokens.text.body, fontWeight: 600 }}>{r.name}</span>
                      <span style={{ display: "block", fontSize: 11, color: tokens.text.faint }}>{r.detail}</span>
                      {r.note && <span style={{ display: "block", fontSize: 11, color: tokens.text.muted, marginTop: 2 }}>{r.note}</span>}
                      {r.flags?.length > 0 && <span style={{ display: "block" }}>{r.flags.map((f, j) => <Chip key={j} text={f} />)}</span>}
                    </span>
                    <span style={{ ...numCol, fontSize: 12, color: tokens.text.body }}>{usd(r.prev)}</span>
                    <span style={{ ...numCol, fontSize: 12, color: tokens.text.muted }}>{usd(r.cur)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 10, padding: "10px 0 0", fontSize: 13, fontWeight: 800, color: tokens.text.primary }}>
        <span style={{ flex: 1, display: "inline-flex", alignItems: "center", gap: 6 }}>Total <span style={{ fontSize: 11.5, fontWeight: 400, color: tokens.text.faint, display: "inline-flex", alignItems: "center", gap: 4 }}><CheckCircle2 size={12} color={tokens.accent.green} />matches the Azure bill</span></span>
        <span style={numCol}>{usd(snap.total.prev)}</span><span style={{ ...numCol, fontWeight: 600 }}>{usd(snap.total.cur)}</span>
      </div>
    </div>
  );
}

function ByService({ snap }) {
  const P = snap.period;
  const svc = [...(snap.byService || [])].sort((a, b) => b.prev - a.prev || b.cur - a.cur);
  const topN = svc.slice(0, 9), rest = svc.slice(9);
  const lines = rest.length ? [...topN, { service: `${rest.length} other services`, prev: rest.reduce((a, s) => a + s.prev, 0), cur: rest.reduce((a, s) => a + s.cur, 0) }] : topN;
  const max = Math.max(...lines.map((l) => l.prev), 1);
  return (
    <div style={{ ...card, padding: "14px 16px", minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: tokens.text.primary }}>Cost by service</div>
      <div style={{ fontSize: 12, color: tokens.text.faint, marginBottom: 10 }}>What kind of Azure service the money goes to, {P.prevLong} (bar) and {P.curShort} so far.</div>
      {lines.map((l) => (
        <div key={l.service} style={{ display: "flex", gap: 10, alignItems: "center", padding: "5px 0" }}>
          <span style={{ width: "38%", minWidth: 0, fontSize: 12.5, color: tokens.text.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.service}>{l.service}</span>
          <span style={{ flex: 1, height: 8, borderRadius: 4, background: tokens.surface.mid, overflow: "hidden" }}>
            <span style={{ display: "block", height: "100%", width: `${Math.max(0.5, (l.prev / max) * 100)}%`, background: tokens.accent.indigo, borderRadius: 4 }} />
          </span>
          <span style={{ width: 74, textAlign: "right", fontSize: 12.5, fontWeight: 600, color: tokens.text.primary, fontVariantNumeric: "tabular-nums" }}>{usd(l.prev)}</span>
          <span style={{ width: 64, textAlign: "right", fontSize: 11.5, color: tokens.text.faint, fontVariantNumeric: "tabular-nums" }}>{usd(l.cur)}</span>
        </div>
      ))}
    </div>
  );
}

function BySub({ snap }) {
  const P = snap.period;
  const subs = [...(snap.bySub || [])].sort((a, b) => b.prev - a.prev);
  return (
    <div style={{ ...card, padding: "14px 16px", minWidth: 0 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: tokens.text.primary }}>Cost by subscription</div>
      <div style={{ fontSize: 12, color: tokens.text.faint, marginBottom: 10 }}>Where Azure sends the bill.</div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
        <thead><tr>
          <th style={{ textAlign: "left", padding: "4px 0", fontSize: 11, color: tokens.text.label, textTransform: "uppercase", letterSpacing: 0.4 }}>Subscription</th>
          <th style={{ textAlign: "right", padding: "4px 0", fontSize: 11, color: tokens.text.label, textTransform: "uppercase", letterSpacing: 0.4 }}>{P.prevShort}</th>
          <th style={{ textAlign: "right", padding: "4px 0", fontSize: 11, color: tokens.text.label, textTransform: "uppercase", letterSpacing: 0.4 }}>{P.curShort} so far</th>
        </tr></thead>
        <tbody>
          {subs.map((s) => (
            <tr key={s.sub} style={{ borderTop: `1px solid ${tokens.border.softer}` }}>
              <td style={{ padding: "7px 0", color: tokens.text.body }}>{s.sub}</td>
              <td style={{ padding: "7px 0", textAlign: "right", fontWeight: 600, color: tokens.text.primary, fontVariantNumeric: "tabular-nums" }}>{usd(s.prev)}</td>
              <td style={{ padding: "7px 0", textAlign: "right", color: tokens.text.body, fontVariantNumeric: "tabular-nums" }}>{usd(s.cur)}</td>
            </tr>
          ))}
          <tr style={{ borderTop: `2px solid ${tokens.border.soft}` }}>
            <td style={{ padding: "7px 0", fontWeight: 800, color: tokens.text.primary }}>Total</td>
            <td style={{ padding: "7px 0", textAlign: "right", fontWeight: 800, color: tokens.text.primary, fontVariantNumeric: "tabular-nums" }}>{usd(snap.total.prev)}</td>
            <td style={{ padding: "7px 0", textAlign: "right", fontWeight: 700, color: tokens.text.primary, fontVariantNumeric: "tabular-nums" }}>{usd(snap.total.cur)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
